import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, Select } from '@devkit/ui';
import { ShieldCheck, ShieldX } from 'lucide-react';
import { verifyHashDetailed, SUPPORTED_HASH_ALGORITHMS, type HashAlgorithm } from '@devkit/tools-security';
import { trackToolExecute, trackClear } from '@devkit/analytics';

const algorithmOptions = SUPPORTED_HASH_ALGORITHMS.map((a) => ({ value: a, label: a }));

export default function HashVerifierPage() {
  const [input, setInput] = useState('');
  const [expectedHash, setExpectedHash] = useState('');
  const [algorithm, setAlgorithm] = useState<HashAlgorithm>('SHA-256');
  const [result, setResult] = useState<{
    match: boolean;
    computed: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const handleVerify = useCallback(async () => {
    setError(null);
    setResult(null);
    setChecking(true);
    try {
      const r = await verifyHashDetailed(input, expectedHash, algorithm);
      if (!r.success || !r.computed) {
        setError(r.error ?? 'Failed to verify hash.');
        return;
      }
      setResult({ match: r.match ?? false, computed: r.computed });
      trackToolExecute('hash-verifier', algorithm);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setChecking(false);
    }
  }, [input, expectedHash, algorithm]);

  const clearAll = useCallback(() => {
    setInput('');
    setExpectedHash('');
    setResult(null);
    setError(null);
    trackClear('hash-verifier');
  }, []);

  return (
    <ToolLayout slug="hash-verifier">
      <Card>
        <CardContent className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">Algorithm</label>
            <Select
              options={algorithmOptions}
              value={algorithm}
              onChange={(v) => setAlgorithm(v as HashAlgorithm)}
              className="w-48"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Original Text or File Contents
            </label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter the text you want to verify..."
              className="w-full h-32 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Expected Hash ({algorithm})
            </label>
            <textarea
              value={expectedHash}
              onChange={(e) => setExpectedHash(e.target.value)}
              placeholder={`Paste the expected ${algorithm} hash here...`}
              className="w-full h-24 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-xs text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y break-all"
            />
          </div>

          <div className="flex gap-2 mb-4">
            <Button onClick={handleVerify} disabled={!input || !expectedHash || checking} className="gap-2">
              <ShieldCheck className="w-4 h-4" />
              {checking ? 'Verifying...' : 'Verify Hash'}
            </Button>
            <Button variant="ghost" onClick={clearAll}>
              Clear
            </Button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {result && (
            <div
              className={`p-5 rounded-lg border ${
                result.match
                  ? 'bg-green-500/10 border-green-500/40'
                  : 'bg-red-500/10 border-red-500/40'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                {result.match ? (
                  <>
                    <ShieldCheck className="w-6 h-6 text-green-400" />
                    <h3 className="text-lg font-semibold text-green-300">Match ✅</h3>
                  </>
                ) : (
                  <>
                    <ShieldX className="w-6 h-6 text-red-400" />
                    <h3 className="text-lg font-semibold text-red-300">Mismatch ❌</h3>
                  </>
                )}
              </div>
              <p className="text-sm text-slate-300 mb-2">
                {result.match
                  ? 'The computed hash matches the expected hash. The content is authentic.'
                  : 'The computed hash does NOT match the expected hash. The content may have been modified or the expected hash is incorrect.'}
              </p>
              <div>
                <p className="text-xs text-slate-500 mb-1">Computed {algorithm}:</p>
                <code className="block text-xs font-mono text-slate-200 break-all bg-slate-900/60 p-3 rounded">
                  {result.computed}
                </code>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolLayout>
  );
}
